import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { User } from 'src/app/_types/auth.types';
import { CustomerAccountType } from 'src/app/enums/customer-account-type.enum';
import {
  FamilyMemberType,
  getFamilyMemberTypeLabel,
} from 'src/app/enums/family-member-type.enum';
import {
  getInsuranceTypeLabel,
  InsuranceType,
} from 'src/app/enums/insurance-type.enum';
import {
  getChangeReasonSortIndex,
  getLeadChangeReasonLabel,
} from 'src/app/enums/lead-change-reason.enum';
import { getCountryName, LanguageCode } from 'src/app/helpers/nationalities';
import { CardComponent } from 'src/app/shared/ui-kit/card/card.component';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

/**
 * « Informations du preneur d'assurance »: who the customer is, who follows
 * them, who is insured and under which branch — the same panel on every
 * record that concerns a customer, fed whatever that record knows.
 *
 * Input-driven, so a host with no mediator service (the cancellation modal)
 * uses it exactly as the insurance-flow one does through its wrapper. Its one
 * dependency is the language cookie, which names the nationality.
 */
@Component({
  selector: 'app-customer-profile-panel',
  standalone: true,
  imports: [CommonModule, RouterModule, CardComponent, ExpansionPanelComponent],
  templateUrl: './customer-profile-panel.component.html',
  styleUrl: './customer-profile-panel.component.css',
})
export class CustomerProfilePanelComponent {
  private readonly cookieService = inject(CookieService);

  readonly title = input($localize`Informations du preneur d’assurance`);

  readonly userProfile = input<User | null>(null);

  readonly familyMemberName = input<string | null>(null);

  readonly relation = input<FamilyMemberType | null>(null);

  readonly insuranceType = input<InsuranceType | null>(null);

  /** The « Action » row, shown only when there is something to say. */
  readonly changeReasons = input<string[]>([]);

  protected readonly languageCode: LanguageCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) || 'fr';

  protected readonly fallbackValue = '-';

  protected get isPersonalAccount(): boolean {
    return this.userProfile()?.type === CustomerAccountType.PERSONAL;
  }

  protected get phoneNumber(): string {
    const user = this.userProfile();
    if (!user) {
      return this.fallbackValue;
    }

    return (
      `${user.countryCode ?? ''} ${user.mobile ?? ''}`.trim() ||
      this.fallbackValue
    );
  }

  protected get nationalityLabel(): string {
    const nationality = this.userProfile()?.nationality;
    if (!nationality) {
      return this.fallbackValue;
    }

    return getCountryName(nationality, this.languageCode);
  }

  protected get relationLabel(): string {
    const relation = this.relation();
    if (!relation) {
      return this.fallbackValue;
    }

    return getFamilyMemberTypeLabel(relation);
  }

  protected get insuranceTypeLabel(): string {
    const insuranceType = this.insuranceType();
    if (!insuranceType) {
      return this.fallbackValue;
    }

    return getInsuranceTypeLabel(insuranceType);
  }

  protected get changeReasonLabel(): string {
    return [...this.changeReasons()]
      .sort((a, b) => getChangeReasonSortIndex(a) - getChangeReasonSortIndex(b))
      .map((reason) => getLeadChangeReasonLabel(reason))
      .join(', ');
  }

  protected asDisplayValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return this.fallbackValue;
    }

    return String(value);
  }
}
